import api from "../api";
import {
  Client,
  ClientCreatePayload,
  ClientsResponse,
  ClientUpdatePayload,
  PlatformUser,
  PlatformUsersResponse,
  PlatformUserCreateResponse,
  AdminFolderAccess,
  AdminStorageFolderListing,
  FolderSubfolderOption,
  PlatformIntegrationsResponse,
  AdminChatBotProfile,
  BackendUser,
  BackendUsersResponse,
  AdminUserIntegrations,
  AdminVendorConnection,
  UserType,
} from "types/interfaces/adminUsers";
import {
  buildLogRecordsQuery,
  buildManualResolutionQuery,
  ErrorScreenshotLink,
  LogRecordsQuery,
  LogRecordsResponse,
  ManualResolutionKey,
  ManualResolutionPreview,
  ManualResolutionSaveRequest,
  ManualResolutionSaveResponse,
} from "components/botsManagement/logRecords";
import {
  buildRosterFileQuery,
  RosterFileQuery,
  RosterFileRecordsResponse,
} from "components/botsManagement/rosterFile";
import {
  buildSsnFileQuery,
  SsnFileQuery,
  SsnFileRecordsResponse,
} from "components/botsManagement/ssnFile";

export interface GetAdminUsersParams {
  page?: number;
  search?: string;
  page_size?: number;
  id?: number;
  role?: string;
  user_type?: UserType;
  client_id?: number;
  is_archived?: boolean;
  sort_by?:
    | "name"
    | "username"
    | "email"
    | "date_joined"
    | "last_login"
    | "folders"
    | "integrations"
    | "sftp"
    | "client_name"
    | "status"
    | "roles"
    | "assigned_clients"
    | "s3_user"
    | "bot_jobs";
  sort_order?: "asc" | "desc";
}

export interface GetClientsParams {
  page?: number;
  search?: string;
  page_size?: number;
  is_active?: boolean;
  sort_by?:
    | "company_name"
    | "company_code"
    | "created_at"
    | "owner"
    | "status"
    | "users";
  sort_order?: "asc" | "desc";
  owner_id?: number;
}

export type BackendUserLabel = "migration" | "integration" | "sftp" | "bots";

export interface GetBackendUsersParams extends GetAdminUsersParams {
  label?: BackendUserLabel;
}

export interface UpdateAdminUserPayload {
  id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_type?: "internal" | "client";
  client_id?: number | null;
  is_data_storage_user?: boolean;
  is_integration_dashboard_user?: boolean;
  is_ai_agent_user?: boolean;
  is_partnership_team?: boolean;
  is_calibrate_manager?: boolean;
  can_download_top_document_folders?: boolean;
  is_secure_file_upload_user?: boolean;
  is_pass_reset?: boolean;
  owned_client_ids?: number[];
}

export interface CreateAdminUserPayload {
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_type?: "internal" | "client";
  client_id?: number | null;
  is_data_storage_user?: boolean;
  is_integration_dashboard_user?: boolean;
  is_ai_agent_user?: boolean;
  is_partnership_team?: boolean;
  can_download_top_document_folders?: boolean;
  is_secure_file_upload_user?: boolean;
  owned_client_ids?: number[];
}

export interface AvailabilityResponse {
  available: boolean;
  message?: string;
}

/**
 * What a stop actually achieved. ECS takes tens of seconds to tear a container
 * down, so "requested" is the honest best case; "already_gone" means the id had
 * outlived its container, and "failed" means the container may still be running.
 */
export type StopOutcome = "requested" | "already_gone" | "failed";

export interface TaskStopResponse {
  message: string;
  outcome?: StopOutcome;
}

export interface BatchStopResponse {
  message: string;
  stopped?: number;
  requested?: number[];
  already_gone?: number[];
  failed?: { task_id: number; error: string }[];
}

/** A run booked for a later moment, as the panel sees it. */
export interface ScheduledRunInfo {
  id: number;
  target_kind: "JOB" | "TASK";
  job_id: number | null;
  task_id: number | null;
  status: "PENDING" | "STARTING" | "STARTED" | "CANCELLED" | "MISSED" | "FAILED";
  /** The instant, ISO-8601 with an offset. */
  scheduled_at: string;
  /** The IANA zone the operator picked it in. */
  timezone: string;
  /** The same moment spelled out in that zone, for the tooltip. */
  scheduled_at_local: string;
  seconds_until_start: number;
  scheduled_by: string;
  reminder_sent_at: string | null;
  outcome_message: string;
}

/** One option a bot reads for itself, declared in BotRegistry.metadata. */
export interface BotOptionSpec {
  key: string;
  label: string;
  type: "string" | "int" | "bool" | "list" | "map";
  help?: string;
  required?: boolean;
  default?: string | number | boolean;
  /** Closed set of accepted values; renders as a select instead of free text. */
  choices?: string[];
  /** A login secret: its values are masked in the form and in every summary. */
  secret?: boolean;
  /** Column placeholders of a map's rows. */
  key_label?: string;
  value_label?: string;
}

export type DocumentTypeUse = "required" | "optional" | "unused";

export interface BotRegistryItem {
  bot_id: string;
  module_path: string;
  vendor: string;
  job_kind: string;
  bot_slug: string;
  supported_job_types?: string[];
  /** How many tasks of a job on this bot should run at once; null = the vendor
   *  meters no sessions, so nothing needs holding back. */
  recommended_parallelism?: number | null;
  /** What this bot does with the job's Document type: "required" (it cannot
   *  run without one), "optional" (blank is a real answer) or "unused" (it
   *  never reads the value, so the form does not ask). */
  document_type_use?: DocumentTypeUse;
  aws_ready: boolean;
  is_active: boolean;
  metadata?: { options?: BotOptionSpec[] } | null;
}

/** Parent grouping for bot automation under a Django user (see `/tasks/major-jobs/`). */
export interface MatchesFileInfo {
  filename: string;
  exists: boolean;
  size_bytes: number | null;
  source: string | null;
  key: string | null;
  last_modified: string | null;
  /** Data rows of the CSV (header excluded); null when the file is missing. */
  rows: number | null;
}

/** Set when the Matches file was uploaded by hand instead of built by the bot. */
export interface MatchesManualUpload {
  uploaded_at: string;
  uploaded_by: string;
  file_name: string;
  rows: number;
}

/** The two report files an operator reviews and merges into the Matches file. */
export type MergeReportKind = "no_matches" | "several_matches";
export const MERGE_REPORT_KINDS: MergeReportKind[] = ["no_matches", "several_matches"];

/** An employee of a No matches / Several matches file (one Data Storage folder),
 *  as the review sees it (CAL-5998): merged once the Matches file lists it,
 *  no match once a reviewer marked it, pending otherwise. */
export type UnmatchedEmployeeState = "pending" | "no_match" | "merged";

export interface UnmatchedEmployeeRow {
  /** Row index in the Matches file - the bot's inner number. */
  index: number;
  employee_id: string;
  /** The task whose current range covers the row - its Logs hold the employee. */
  task_id: number | null;
}

export interface UnmatchedEmployeeStatus {
  s3_name: string;
  state: UnmatchedEmployeeState;
  /** Rows of the employee in its file (Several matches lists one per candidate). */
  candidates: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewed_source: "mark" | "merge" | null;
  /** Merged only: the Matches rows the employee holds. */
  rows: UnmatchedEmployeeRow[];
}

/** Counts over one file's employees (never names). */
export interface UnmatchedKindSummary {
  employees: number;
  pending: number;
  no_match: number;
  merged: number;
  marks_total: number;
  stale_marks: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

/** TaskJob.unmatched_review, and the `review` block of the matches-files listing. */
export interface UnmatchedReview {
  version: number;
  computed_at: string;
  no_matches: UnmatchedKindSummary | null;
  several_matches: UnmatchedKindSummary | null;
}

export interface UnmatchedReviewDetail {
  job_id: number;
  review: UnmatchedReview;
  files: Record<MergeReportKind, { exists: boolean; employees: number }>;
  statuses: Record<MergeReportKind, Record<string, UnmatchedEmployeeStatus>>;
}

export type UnmatchedReviewAction = "mark" | "clear";

export interface UnmatchedReviewMarkResponse {
  job_id: number;
  kind: MergeReportKind;
  action: UnmatchedReviewAction;
  marked?: number;
  already_reviewed?: number;
  skipped_merged?: number;
  cleared?: number;
  review: UnmatchedReview;
  statuses: Record<MergeReportKind, Record<string, UnmatchedEmployeeStatus>>;
}

/** One thing wrong with a reviewed file: what it is, and how the file should look. */
export interface MergeFileProblem {
  code: string;
  message: string;
  hint: string;
  details?: Record<string, unknown>;
}

/** The backend's check of one reviewed file (see merge_validation.FileReport). */
export interface MergeFileReport {
  kind: MergeReportKind;
  file_name: string;
  status: "ok" | "blocked" | "merged";
  columns: number;
  rows_total: number;
  rows_to_add: number;
  rows_added: number;
  rows_no_match: number;
  rows_blank_id: number;
  rows_already_present: number;
  /** Employees whose every row says No Match - the merge marks them reviewed. */
  no_match_employees?: string[];
  problems: MergeFileProblem[];
  notes: string[];
}

export interface MergeReportsResponse {
  rows_added: number;
  rows_skipped: number;
  matches_filename: string;
  actual_total_employees: number | null;
  merged_employees_from: number | null;
  files: Partial<Record<MergeReportKind, MergeFileReport>>;
  /** No Match employees the merge recorded as reviewed, per file. */
  no_match_marked?: Partial<Record<MergeReportKind, number>>;
  review?: UnmatchedReview | null;
}

/**
 * Whether the row numbers a job's ranges are written in still mean the same
 * people. "appended" is a Merge report (the numbering is intact), "reordered"
 * the same people on other rows, "lost" a file that no longer holds employees
 * the logs have records for - which is never re-covered on its own.
 */
export interface RosterState {
  fingerprint: string;
  rows: number;
  state: "unchanged" | "appended" | "reordered" | "lost" | "unknown";
  checked_at?: string;
  lost_employees?: number;
  // As the last read of the logs counted them; the number that follows the run
  // is stats.range_coverage.never_run_employees.
  never_run_employees?: number;
  never_run_duplicate_rows?: number;
  never_run_counted_at?: string;
  records_not_in_roster?: number;
  needs_recover?: boolean;
  recovered_at?: string | null;
  renumbered_at?: string | null;
  blocked_by_jobs?: number[];
}

/** The file a run reuses: the roster (download, delete) or the matches files (upload). */
export type JobRosterInfo =
  | {
      kind: "roster";
      key: string;
      exists: boolean;
      size_bytes: number | null;
      last_modified: string | null;
      rows: number | null;
      sibling_job_ids: number[];
    }
  | {
      kind: "matches";
      files: MatchesFileInfo[];
      summary: MatchesSummary | null;
      sibling_job_ids: number[];
    };

/** "Send rejected files to SFTP": what the job's file logs say could not be
 *  uploaded, the SFTP connection that hands it to the client, and the export
 *  manifest the copy writes as it goes. */
export interface SftpExportEligibility {
  ok: boolean;
  code:
    | "ok"
    | "not_upload_job"
    | "tasks_in_flight"
    | "no_total"
    | "not_finished"
    | "no_error_employees";
  reason: string;
  total: number | null;
  success: number;
  error: number;
}

export interface SftpExportUnresolved {
  employee_name: string | null;
  employee_id: string | null;
  filename: string;
  reason: string;
}

export interface SftpExportPreview {
  mode: "copy" | "zip";
  rejected_files: number;
  entries: number;
  employees: number;
  unresolved: SftpExportUnresolved[];
  error_employees_without_files: number;
  reasons: Record<string, number>;
}

export interface SftpExportConnection {
  id: number;
  name: string;
  username: string;
  role: string;
  is_active: boolean;
  has_password: boolean;
  label: string;
  prefix: string;
}

export interface SftpExportSftpInfo {
  host: string;
  server_id: string;
  root_prefix: string;
  existing: SftpExportConnection[];
  inactive: SftpExportConnection[];
  proposed: {
    name: string;
    username: string;
    username_available: boolean;
    suggested_username: string;
  };
}

export interface SftpExportEntry {
  kind: "copy" | "zip";
  employee_name: string;
  employee_id: string | null;
  s3_employee_folder: string;
  dest_employee_folder: string;
  source_key: string;
  source_root: string | null;
  dest_key: string;
  status: "pending" | "copied" | "failed";
  resolution: string | null;
  error: string | null;
  size: number | null;
  reasons: string[];
  records: number;
}

export type SftpExportState = "none" | "running" | "interrupted" | "done" | "failed";

export interface SftpExportManifest {
  version: number;
  job_id: number;
  export_id: string;
  previous_export_id: string | null;
  status: "running" | "done" | "failed";
  state: SftpExportState;
  progress_pct: number;
  started_at: string;
  heartbeat_at: string;
  finished_at: string | null;
  started_by: { id: number; username: string | null } | null;
  mode: "copy" | "zip";
  source_base_prefix: string;
  dest_prefix: string;
  document_type: string | null;
  sftp_connection_id: number | null;
  counts: {
    planned: number;
    done: number;
    copied: number;
    failed: number;
    records: number;
    employees: number;
    unresolved: number;
    bytes: number;
  };
  error: string | null;
  files?: SftpExportEntry[];
  unresolved: SftpExportUnresolved[];
}

export interface SftpExportStatus {
  job_id: number;
  mode: "copy" | "zip";
  source_base_prefix: string;
  root_prefix: string;
  dest_prefix: string;
  eligibility: SftpExportEligibility;
  sftp: SftpExportSftpInfo;
  export: SftpExportManifest | null;
  preview?: SftpExportPreview;
}

export type SftpExportStartRequest =
  | { mode: "reuse"; connection_id: number }
  | { mode: "create"; username: string; name: string };

export interface SftpExportStartResponse {
  export: SftpExportManifest;
  sftp: {
    id: number;
    host: string;
    server_id: string;
    username: string;
    name: string;
    created: boolean;
    password: string | null;
    has_password: boolean;
  };
}

export interface SftpExportFile {
  key: string;
  name: string;
  size: number;
  last_modified: string | null;
}

export interface SftpExportFilesResponse {
  job_id: number;
  scope: "job" | "root";
  root_prefix: string;
  dest_prefix: string;
  total_files: number;
  total_bytes: number;
  truncated: boolean;
  groups: {
    document_type: string;
    employees: { folder: string; files: SftpExportFile[] }[];
  }[];
}

export interface SsnFileFacts {
  vendor: string;
  key: string;
  exists: boolean;
  size_bytes: number | null;
  last_modified: string | null;
  empty: boolean;
}

/** Would an upload run match by SSN? Both SSN files must exist and hold data. */
export interface SsnReadiness {
  source_vendor: string;
  source: SsnFileFacts | null;
  destination_vendor: string;
  destination: SsnFileFacts;
  use_ssn_match: boolean;
  ready: boolean;
  algorithm: "ssn" | "names";
  reason: string;
  /** Matching only happens when the Matches file has to be built. */
  matches_file_exists?: boolean;
}

export interface BrowserProfileInfo {
  key: string;
  profile_id: number;
  size_bytes: number | null;
  last_modified: string | null;
}

export interface BrowserProfilesInfo {
  vendor: string;
  profiles: BrowserProfileInfo[];
  jobs_using: number;
  in_flight_task_ids: number[];
}

/** What deleting a major job removes. The S3 counts are null when S3 could not
 * be read: unknown, not zero. */
export interface MajorJobDeletePreview {
  task_jobs: number;
  tasks: number;
  log_archives: number | null;
  other_files: number | null;
  /** Jobs outside this major job still reading its shared roster file, which stays. */
  roster_kept_for_job_ids?: number[];
}

export interface MatchesSummarySsnFile {
  vendor: string;
  key: string;
  last_modified: string | null;
  ssn_count: number;
  account_count: number;
  employee_id_shapes: string[];
}

/** Written by the bot next to the matches CSVs: how the roster was built. */
export interface MatchesSummary {
  version: number;
  job_id: string | null;
  task_id: string | null;
  created_at: string;
  algorithm: string;
  ssn_match_requested: boolean;
  ssn_fallback_reason: string;
  s3_folders: number;
  matched_rows: number;
  matched_folders: number;
  several: number;
  no_matches: number;
  breakdown: Record<string, number>;
  top_reason: string;
  top_reason_label: string;
  hints: string[];
  ssn_files: Record<string, MatchesSummarySsnFile>;
  folder_id_shapes: string[];
  options: Record<string, unknown>;
  manual_upload?: MatchesManualUpload | null;
}

export interface JobMatchesFilesResponse {
  files: MatchesFileInfo[];
  summary: MatchesSummary | null;
  review?: UnmatchedReview | null;
}

/** What SSN Updater would do with the job's folders (counts only, no names). */
export interface SsnUpdaterEstimate {
  folders: number;
  four_digit_tails: number;
  linkable_exact: number;
  linkable_similar: number;
  already_linked: number;
  ambiguous: number;
  no_last4: number;
  no_name_match: number;
  full_ssn_tails: number;
  not_ssn_tails: number;
  no_separator: number;
  /** Linked folders whose SSN the destination file knows; null without that file. */
  would_match: number | null;
  tail_shapes: string[];
}

/** The last SSN Updater run of the job, as the job row remembers it. */
export interface SsnUpdaterSummary {
  status: "running" | "done" | "failed";
  run_id: string;
  started_at?: string | null;
  finished_at?: string | null;
  allow_similar?: boolean;
  added?: number | null;
  error?: string | null;
}

/**
 * The pre-run check of an upload job's employee folders against the SSN files
 * (TaskJob.matches_preflight): the join the bot will run, computed up front.
 */
export interface MatchesPreflight {
  status: "running" | "done" | "error";
  run_id?: string;
  trigger?: string | null;
  started_at?: string | null;
  checked_at?: string | null;
  algorithm?: "ssn" | "names";
  reason?: string;
  source_vendor?: string;
  destination_vendor?: string;
  matches_file_exists?: boolean;
  s3_folders?: number | null;
  index_folders?: number | null;
  segment_applied?: boolean;
  base_level_files?: number;
  unsplittable?: number;
  matched_folders?: number | null;
  matched_rows?: number | null;
  no_matches?: number | null;
  breakdown?: Record<string, number>;
  top_reason?: string;
  top_reason_label?: string;
  folder_id_shapes?: string[];
  source_id_shapes?: string[];
  updater_estimate?: SsnUpdaterEstimate | null;
  suggest_ssn_updater?: boolean;
  ssn_updater?: SsnUpdaterSummary | null;
  error?: string | null;
}

export interface SsnUpdaterEntry {
  folder: string;
  outcome: string;
  score: number | null;
}

/** The manifest of one SSN Updater run (S3), with its derived state. */
export interface SsnUpdaterRun {
  version: number;
  run_id: string;
  job_id: number;
  status: "running" | "done" | "failed";
  state: "running" | "interrupted" | "done" | "failed";
  started_at: string;
  heartbeat_at: string | null;
  finished_at: string | null;
  worker_started_at: string | null;
  started_by: { id: number; username: string | null } | null;
  allow_similar: boolean;
  source_vendor: string;
  destination_vendor: string;
  ssn_file_key: string;
  ssn_file_version_before: string | null;
  ssn_file_version_after: string | null;
  folders: number | null;
  counts: Partial<SsnUpdaterEstimate> & { added?: number };
  entries?: SsnUpdaterEntry[];
  error: string | null;
}

export interface SsnUpdaterStatus {
  job_id: number;
  readiness: SsnReadiness;
  matches_preflight: MatchesPreflight | null;
  run: SsnUpdaterRun | null;
}

export interface MajorJob {
  id: number;
  user: number;
  name: string;
  vendor: string;
  bot_kind: string;
  included_connection_ids: number[];
  child_job_ids: number[];
  ssn_file_exists: boolean | null;
  /** A bot of this automation is in a vendor's portal right now, so a delete
   * would be refused. Sent by the list and detail reads. */
  has_running_tasks?: boolean;
}

// Tells the backend an operator is watching: 2FA and the captcha stay in the
// panel. Runs started anywhere else fall back to Slack.
const PANEL_RUN_HEADERS = { "X-Run-Source": "panel" };

const PLATFORM_BASE = "/api/admin/platform-users";
const BACKEND_BASE = "/api/admin/users";
const CLIENTS_BASE = "/api/admin/clients";

const adminUsersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<PlatformUsersResponse, GetAdminUsersParams>({
      query: ({
        page = 1,
        search,
        page_size,
        role,
        user_type,
        client_id,
        is_archived,
        sort_by,
        sort_order,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (role) params.set("role", role);
        if (user_type) params.set("user_type", user_type);
        if (client_id) params.set("client_id", String(client_id));
        if (is_archived !== undefined)
          params.set("is_archived", String(is_archived));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        return `${PLATFORM_BASE}/?${params.toString()}`;
      },
      providesTags: ["AdminUsers"],
    }),

    getClients: builder.query<ClientsResponse, GetClientsParams>({
      query: ({
        page = 1,
        search,
        page_size,
        is_active,
        sort_by,
        sort_order,
        owner_id,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (is_active !== undefined) params.set("is_active", String(is_active));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        if (owner_id) params.set("owner_id", String(owner_id));
        return `${CLIENTS_BASE}/?${params.toString()}`;
      },
      providesTags: ["AdminClients"],
    }),

    getClient: builder.query<Client, number>({
      query: (id) => `${CLIENTS_BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminClients", id }],
    }),

    createClient: builder.mutation<Client, ClientCreatePayload>({
      query: (body) => ({
        url: `${CLIENTS_BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminClients"],
    }),

    updateClient: builder.mutation<
      Client,
      { id: number } & ClientUpdatePayload
    >({
      query: ({ id, ...body }) => ({
        url: `${CLIENTS_BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdminClients", id },
        "AdminClients",
        "AdminUsers",
      ],
    }),

    archiveClient: builder.mutation<
      Client,
      { id: number; archiveDate?: string | null }
    >({
      query: ({ id, archiveDate }) => ({
        url: `${CLIENTS_BASE}/${id}/archive/`,
        method: "PATCH",
        body: archiveDate !== undefined ? { archive_date: archiveDate } : {},
      }),
      invalidatesTags: ["AdminClients", "AdminUsers"],
    }),

    checkClientCodeAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${CLIENTS_BASE}/check-code/?${params.toString()}`;
      },
    }),

    checkClientNameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${CLIENTS_BASE}/check-name/?${params.toString()}`;
      },
    }),

    getAdminUser: builder.query<PlatformUser, number>({
      query: (id) => `${PLATFORM_BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id }],
    }),

    checkPlatformUsernameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${PLATFORM_BASE}/check-username/?${params.toString()}`;
      },
    }),

    checkPlatformEmailAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${PLATFORM_BASE}/check-email/?${params.toString()}`;
      },
    }),

    updateAdminUser: builder.mutation<PlatformUser, UpdateAdminUserPayload>({
      query: ({ id, ...body }) => ({
        url: `${PLATFORM_BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        "AdminUsers",
        "Auth",
        { type: "FileSystem" as const, id: "__root__" },
      ],
    }),

    createAdminUser: builder.mutation<PlatformUserCreateResponse, CreateAdminUserPayload>({
      query: (body) => ({
        url: `${PLATFORM_BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    archiveAdminUser: builder.mutation<
      PlatformUser,
      { id: number; archiveDate?: string | null }
    >({
      query: ({ id, archiveDate }) => ({
        url: `${PLATFORM_BASE}/${id}/archive/`,
        method: "PATCH",
        body: archiveDate !== undefined ? { archive_date: archiveDate } : {},
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    resetAdminUserPassword: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `${PLATFORM_BASE}/${id}/reset-password/`,
        method: "POST",
      }),
    }),

    generateAdminUserPassword: builder.mutation<{ generated_password: string }, number>({
      query: (id) => ({
        url: `${PLATFORM_BASE}/${id}/generate-password/`,
        method: "POST",
      }),
    }),

    getAdminUserFolders: builder.query<
      AdminFolderAccess[],
      { id: number; client_id?: number }
    >({
      query: ({ id, client_id }) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/${id}/folders/${params}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "AdminUsers", id: `folders-${id}` },
      ],
    }),

    getAssignableFolders: builder.query<
      AdminFolderAccess[],
      number | undefined
    >({
      query: (client_id) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/assignable-folders/${params}`;
      },
      providesTags: ["AdminUsers"],
    }),

    getAssignableIntegrations: builder.query<
      PlatformIntegrationsResponse,
      number | undefined
    >({
      query: (client_id) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/assignable-integrations/${params}`;
      },
      providesTags: ["AdminUsers"],
    }),

    grantFolderAccess: builder.mutation<
      void,
      { userId: number; folderId: number; allowedSubfolders?: string[] }
    >({
      query: ({ userId, folderId, allowedSubfolders }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/grant/`,
        method: "POST",
        body: { allowed_subfolders: allowedSubfolders ?? [] },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        "AdminUsers",
      ],
    }),

    updateFolderSubfolders: builder.mutation<
      void,
      { userId: number; folderId: number; allowedSubfolders: string[] }
    >({
      query: ({ userId, folderId, allowedSubfolders }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/subfolders/`,
        method: "PATCH",
        body: { allowed_subfolders: allowedSubfolders },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
      ],
    }),

    getFolderSubfolders: builder.query<
      { subfolders: FolderSubfolderOption[] },
      number
    >({
      query: (folderId) => `${PLATFORM_BASE}/folders/${folderId}/subfolders/`,
    }),

    revokeFolderAccess: builder.mutation<void, { userId: number; folderId: number }>({
      query: ({ userId, folderId }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminUserIntegrations: builder.query<
      PlatformIntegrationsResponse,
      { id: number; client_id?: number }
    >({
      query: ({ id, client_id }) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/${id}/integrations/${params}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "AdminUsers", id: `integrations-${id}` },
      ],
    }),

    grantIntegrationAccess: builder.mutation<void, { userId: number; matchId: number }>({
      query: ({ userId, matchId }) => ({
        url: `${PLATFORM_BASE}/${userId}/integrations/${matchId}/grant/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        "AdminUsers",
      ],
    }),

    revokeIntegrationAccess: builder.mutation<void, { userId: number; matchId: number }>({
      query: ({ userId, matchId }) => ({
        url: `${PLATFORM_BASE}/${userId}/integrations/${matchId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminChatBotProfile: builder.query<AdminChatBotProfile, number>({
      query: (userId) => `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
      providesTags: (_result, _error, userId) => [{ type: "AdminUsers", id: `chat-bot-profile-${userId}` }],
    }),

    createAdminChatBotProfile: builder.mutation<
      AdminChatBotProfile,
      { userId: number; chat_id?: string; api_key?: string; name?: string; email?: string }
    >({
      query: ({ userId, ...body }) => ({
        url: `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
        "AdminUsers",
      ],
    }),

    updateAdminChatBotProfile: builder.mutation<
      AdminChatBotProfile,
      { userId: number; chat_id?: string; api_key?: string }
    >({
      query: ({ userId, ...body }) => ({
        url: `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
      ],
    }),

    updateAdminUserPermissions: builder.mutation<
      PlatformUser,
      {
        id: number;
        is_calibrate_manager: boolean;
        can_download_top_document_folders: boolean;
        is_partnership_team?: boolean;
        is_developer?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `${PLATFORM_BASE}/${id}/permissions/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    getBackendUsers: builder.query<BackendUsersResponse, GetBackendUsersParams>({
      query: ({ page = 1, search, page_size, id, sort_by, sort_order, label } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (id != null) params.set("id", String(id));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        if (label) params.set("label", label);
        return `${BACKEND_BASE}/?${params.toString()}`;
      },
      transformResponse: (response: {
        count: number;
        next: string | null;
        previous: string | null;
        results: Array<{
          id: number;
          username: string;
          first_name: string;
          last_name: string;
          client_name?: string | null;
          client_id?: number | null;
          folder_count: number;
          integration_count: number;
          platform_integration_count?: number;
          sftp_connection_count?: number;
          major_job_count?: number;
          date_joined: string;
          last_login: string | null;
        }>;
      }): BackendUsersResponse => ({
        count: response.count,
        next: response.next,
        previous: response.previous,
        results: response.results.map((u) => ({
          id: u.id,
          username: u.username,
          first_name: u.first_name ?? "",
          last_name: u.last_name ?? "",
          client_name: u.client_name ?? null,
          client_id: u.client_id ?? null,
          folder_count: u.folder_count,
          integration_count: u.integration_count,
          platform_integration_count: u.platform_integration_count ?? 0,
          sftp_count: u.sftp_connection_count ?? 0,
          major_job_count: u.major_job_count ?? 0,
          date_joined: u.date_joined,
          last_login: u.last_login,
        })),
      }),
      providesTags: ["AdminUsers"],
    }),

    checkBackendUsernameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${BACKEND_BASE}/check-username/?${params.toString()}`;
      },
    }),

    checkBackendEmailAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${BACKEND_BASE}/check-email/?${params.toString()}`;
      },
    }),

    getBackendUserFolders: builder.query<AdminFolderAccess[], number>({
      query: (id) => `${BACKEND_BASE}/${id}/folders/owned/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `backend-folders-${id}` }],
    }),

    /** One level of a backend user's Data Storage; no prefix = the folder roots. */
    getBackendUserStorageFolders: builder.query<
      AdminStorageFolderListing,
      { id: number; prefix?: string; search?: string; limit?: number }
    >({
      query: ({ id, prefix, search, limit }) => {
        const params = new URLSearchParams();
        if (prefix) params.set("prefix", prefix);
        if (search) params.set("search", search);
        if (limit) params.set("limit", String(limit));
        const qs = params.toString();
        return `${BACKEND_BASE}/${id}/storage-folders/${qs ? `?${qs}` : ""}`;
      },
      providesTags: (_result, _error, { id, prefix }) => [
        { type: "AdminUsers", id: `storage-folders-${id}-${prefix || "__root__"}` },
      ],
    }),

    getBackendUserIntegrations: builder.query<AdminUserIntegrations, number>({
      query: (id) => `${BACKEND_BASE}/${id}/integrations/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `backend-integrations-${id}` }],
    }),

    getBackendUserBotConnections: builder.query<
      { connections: AdminVendorConnection[] },
      number
    >({
      query: (id) => `${BACKEND_BASE}/${id}/bot-connections/`,
      providesTags: (_result, _error, id) => [
        { type: "AdminUsers", id: `backend-bot-connections-${id}` },
      ],
    }),

    createBackendUserBotConnection: builder.mutation<
      AdminVendorConnection,
      { userId: number; body: Partial<AdminVendorConnection> }
    >({
      query: ({ userId, body }) => ({
        url: `${BACKEND_BASE}/${userId}/bot-connections/`,
        method: "POST",
        body: {
          host: body.host ?? "",
          client_code: body.client_code ?? "",
          username: body.username ?? "",
          password: body.password ?? "",
          port: body.port ?? "9223",
          vendor: body.vendor ?? "",
        },
      }),
    }),

    createConnectionTask: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({
        url: "/tasks/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    addTasksToJob: builder.mutation<
      { tasks: Record<string, unknown>[] },
      { jobId: number; count: number; connection_id?: number }
    >({
      query: ({ jobId, count, connection_id }) => ({
        url: `/tasks/jobs/${jobId}/`,
        method: "POST",
        body:
          connection_id != null ? { count, connection_id } : { count },
      }),
      invalidatesTags: ["BotJobData"],
    }),

    // Fewer tasks on the job. Not a delete: the removed tasks leave their log
    // archives behind, so their employees stay counted and the next run skips
    // them. The server picks which tasks go unless task_ids says otherwise.
    reduceJobTasks: builder.mutation<
      {
        job_id: number;
        removed_task_ids: number[];
        kept_log_task_ids: number[];
        kept_task_ids: number[];
        deleted_task_ids: number[];
        cancelled_scheduled_run_ids: number[];
        job: Record<string, unknown>;
        tasks: Record<string, unknown>[];
      },
      { jobId: number; count?: number; task_ids?: number[] }
    >({
      query: ({ jobId, count, task_ids }) => ({
        url: `/tasks/jobs/${jobId}/reduce-tasks/`,
        method: "POST",
        body: task_ids != null ? { task_ids } : { count },
      }),
      invalidatesTags: ["BotJobData"],
    }),

    createTasksBatch: builder.mutation<
      { tasks: Record<string, unknown>[]; job?: Record<string, unknown> },
      Record<string, unknown>
    >({
      query: (body) => ({
        url: "/tasks/batch/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    getBotRegistry: builder.query<
      { count?: number; results?: BotRegistryItem[] } | BotRegistryItem[],
      { vendor?: string; jobKind?: string; isActive?: boolean; awsReady?: boolean; pageSize?: number }
    >({
      query: ({ vendor, jobKind, isActive = true, awsReady, pageSize = 200 }) => {
        const params = new URLSearchParams();
        if (vendor) params.append("vendor", vendor);
        if (jobKind) params.append("job_kind", jobKind);
        if (isActive != null) params.append("is_active", String(isActive));
        if (awsReady != null) params.append("aws_ready", String(awsReady));
        params.append("page_size", String(pageSize));
        return `/api/admin/bots/?${params.toString()}`;
      },
    }),

    getTaskJobs: builder.query<Record<string, unknown>[], { connectionId?: number; userId?: number; botName?: string; majorJobId?: number; includeTasks?: boolean }>({
      query: ({ connectionId, userId, botName, majorJobId, includeTasks }) => {
        const params = new URLSearchParams();
        if (connectionId != null) params.append("connection_id", String(connectionId));
        if (userId != null) params.append("user_id", String(userId));
        if (botName) params.append("bot_name", botName);
        if (majorJobId != null) params.append("major_job_id", String(majorJobId));
        // Embeds each job's task rows (detail-endpoint shape); the backend
        // honors it only for a scoped list (major_job_id or connection_id).
        if (includeTasks) params.append("include", "tasks");
        params.append("page_size", "100");
        return `/tasks/jobs/?${params.toString()}`;
      },
      providesTags: ["BotJobData"],
    }),

    // Deliberately provides no tags: nothing subscribes to this endpoint - every
    // caller is an imperative initiate({ subscribe: false, forceRefetch: true }).
    // A "BotJobData" tag here let a deferred invalidation (e.g. from runTasksBatch)
    // evict the zero-subscriber entry between fulfillment and unwrap(), making
    // unwrap() resolve to undefined and the panel report a refresh failure.
    getTaskJob: builder.query<Record<string, unknown>, number>({
      query: (jobId) => `/tasks/jobs/${jobId}/`,
    }),

    updateTaskJob: builder.mutation<
      Record<string, unknown>,
      { jobId: number; body: Record<string, unknown> }
    >({
      query: ({ jobId, body }) => ({
        url: `/tasks/jobs/${jobId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    refreshTaskJobStats: builder.mutation<Record<string, unknown>, number>({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/refresh-stats/`,
        method: "POST",
      }),
    }),


    recalculateTaskJobRanges: builder.mutation<
      { job: Record<string, unknown>; tasks: Record<string, unknown>[] },
      { jobId: number; employee_number_from?: number; employee_number_to?: number }
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/recalculate-ranges/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    deleteTaskJob: builder.mutation<
      {
        message: string;
        deleted_task_ids: number[];
        deleted_artifacts_count: number;
        // Jobs still reading the shared roster file, which therefore stayed.
        roster_kept_for_job_ids?: number[];
      },
      { jobId: number }
    >({
      query: ({ jobId }) => ({
        url: `/tasks/jobs/${jobId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    // The same run, booked for later. The body carries the instant plus the zone
    // the operator read it in, so the Slack reminder can say it back in their clock.
    scheduleJobRun: builder.mutation<
      ScheduledRunInfo,
      { jobId: number; scheduled_at: string; timezone: string }
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/schedule/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    cancelJobRunSchedule: builder.mutation<ScheduledRunInfo, number>({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/schedule/`,
        method: "DELETE",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    scheduleTaskRun: builder.mutation<
      ScheduledRunInfo,
      { taskId: number; scheduled_at: string; timezone: string }
    >({
      query: ({ taskId, ...body }) => ({
        url: `/tasks/${taskId}/schedule/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    cancelTaskRunSchedule: builder.mutation<ScheduledRunInfo, number>({
      query: (taskId) => ({
        url: `/tasks/${taskId}/schedule/`,
        method: "DELETE",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    runTasksBatch: builder.mutation<
      {
        message: string;
        started_task_ids?: number[];
        queued_task_ids?: number[];
        /** Error mode only: tasks whose range holds no error employee. */
        left_alone_task_ids?: number[];
      },
      Record<string, unknown>
    >({
      query: (body) => ({
        url: "/tasks/batch/run/",
        method: "POST",
        headers: PANEL_RUN_HEADERS,
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    // A stop is only finished once the container is gone, so the answer is
    // per task: "failed" names the ones ECS refused to stop (CAL-5949).
    stopTasksBatch: builder.mutation<BatchStopResponse, Record<string, unknown>>({
      query: (body) => ({
        url: "/tasks/batch/stop/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    // "Admin" in the endpoint names is load-bearing, not decoration:
    // endpoint names are GLOBAL across the one shared createApi, and with
    // overrideExisting=false RTK silently drops a same-named endpoint from
    // another slice. Plain updateConnection/deleteConnection collided with
    // integrationsBeta/connections.ts, so the integrations-beta UI was
    // invisibly routed to these legacy VendorConnection endpoints (prod
    // incident, 2026-08-22).
    updateAdminConnection: builder.mutation<
      AdminVendorConnection,
      { connectionId: number; body: Partial<AdminVendorConnection> }
    >({
      query: ({ connectionId, body }) => ({
        url: `/api/admin/connections/${connectionId}/`,
        method: "PATCH",
        body,
      }),
    }),

    deleteAdminConnection: builder.mutation<void, number>({
      query: (connectionId) => ({
        url: `/api/admin/connections/${connectionId}/`,
        method: "DELETE",
      }),
    }),

    runTask: builder.mutation<{ message: string }, number>({
      query: (taskId) => ({
        url: `/api/admin/tasks/${taskId}/run/`,
        method: "POST",
        headers: PANEL_RUN_HEADERS,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    stopTask: builder.mutation<TaskStopResponse, number>({
      query: (taskId) => ({
        url: `/api/admin/tasks/${taskId}/stop/`,
        method: "POST",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    setTaskPending: builder.mutation<
      { id: number; task_status: string; bot_work_status: string },
      number
    >({
      query: (taskId) => ({
        url: `/api/admin/tasks/${taskId}/set-pending/`,
        method: "PATCH",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    getAdminTask: builder.query<Record<string, unknown>, number>({
      query: (taskId) => `/api/admin/tasks/${taskId}/`,
      providesTags: ["BotJobData"],
    }),

    updateAdminTask: builder.mutation<
      Record<string, unknown>,
      { taskId: number; body: Record<string, unknown> }
    >({
      query: ({ taskId, body }) => ({
        url: `/tasks/${taskId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),

    deleteTask: builder.mutation<void, number>({
      query: (taskId) => ({
        url: `/tasks/${taskId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    getTask2FAStatus: builder.query<
      {
        task_id: number;
        status: string;
        has_code: boolean;
        requested_at?: string;
        expires_at?: string;
        submitted_at?: string;
        consumed_at?: string;
        auto_until?: string | null;
      },
      number
    >({
      query: (taskId) => `/tasks/${taskId}/2fa/`,
      providesTags: ["BotJobData"],
    }),

    getTaskLiveView: builder.query<
      {
        task_id: number;
        status: string;
        phase?: string;
        slot_index?: number | null;
        instruction?: string;
        live_view_url?: string | null;
        started_by?: number | null;
        started_by_username?: string | null;
        created_at?: string;
        expires_at?: string;
        last_seen_at?: string | null;
        closed_at?: string | null;
        error_message?: string;
      },
      number
    >({
      // The URL carries the token that opens the session, so it comes only
      // from this detail endpoint and never from a task list payload.
      query: (taskId) => `/tasks/${taskId}/live-view/`,
      providesTags: ["BotJobData"],
    }),

    releaseTaskLiveView: builder.mutation<
      { task_id: number; status: string },
      number
    >({
      query: (taskId) => ({
        url: `/tasks/${taskId}/live-view/release/`,
        method: "POST",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    submitTask2FACode: builder.mutation<
      {
        task_id: number;
        status: string;
        has_code: boolean;
      },
      { taskId: number; code: string }
    >({
      query: ({ taskId, code }) => ({
        url: `/tasks/${taskId}/2fa/`,
        method: "PATCH",
        body: { code },
      }),
      invalidatesTags: ["BotJobData"],
    }),

    createBackendUser: builder.mutation<
      BackendUser,
      {
        username: string;
        first_name: string;
        client_id: number;
      }
    >({
      query: ({ username, first_name, client_id }) => ({
        url: `${BACKEND_BASE}/`,
        method: "POST",
        body: {
          username,
          first_name,
          client_id,
          user_type: "backend",
        },
      }),
      transformResponse: (
        u: PlatformUser & {
          sftp_connection_count?: number;
          platform_integration_count?: number;
          major_job_count?: number;
        },
      ): BackendUser => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name ?? "",
        last_name: u.last_name ?? "",
        client_name: u.client_name ?? null,
        client_id: u.client_id ?? null,
        folder_count: u.folder_count,
        integration_count: u.integration_count,
        platform_integration_count: u.platform_integration_count ?? 0,
        sftp_count: u.sftp_connection_count ?? 0,
        major_job_count: u.major_job_count ?? 0,
        date_joined: u.date_joined,
        last_login: u.last_login,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    updateBackendUser: builder.mutation<
      BackendUser,
      {
        id: number;
        username?: string;
        first_name?: string;
        client_id?: number;
      }
    >({
      query: ({ id, username, first_name, client_id }) => ({
        url: `${BACKEND_BASE}/${id}/`,
        method: "PATCH",
        body: {
          ...(username !== undefined ? { username } : {}),
          ...(first_name !== undefined ? { first_name } : {}),
          ...(client_id !== undefined ? { client_id } : {}),
        },
      }),
      transformResponse: (
        u: PlatformUser & {
          sftp_connection_count?: number;
          platform_integration_count?: number;
          major_job_count?: number;
        },
      ): BackendUser => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name ?? "",
        last_name: u.last_name ?? "",
        client_name: u.client_name ?? null,
        client_id: u.client_id ?? null,
        folder_count: u.folder_count,
        integration_count: u.integration_count,
        platform_integration_count: u.platform_integration_count ?? 0,
        sftp_count: u.sftp_connection_count ?? 0,
        major_job_count: u.major_job_count ?? 0,
        date_joined: u.date_joined,
        last_login: u.last_login,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    deleteBackendUser: builder.mutation<
      { message: string; s3_cleanup_queued?: boolean; job_id?: string },
      number
    >({
      query: (id) => ({
        url: `${BACKEND_BASE}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    /** Parent automation grouping (connections pool + child task jobs). */
    getMajorJobs: builder.query<MajorJob[], number>({
      query: (userId) => `/tasks/major-jobs/?user_id=${userId}`,
      transformResponse: (response: MajorJob[] | { results?: MajorJob[] } | null | undefined) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        return Array.isArray(response.results) ? response.results : [];
      },
      providesTags: (_result, _error, userId) => [{ type: "MajorJobs", id: userId }],
    }),

    createMajorJob: builder.mutation<
      MajorJob,
      {
        userId: number;
        name: string;
        vendor: string;
        bot_kind: string;
        included_connection_ids?: number[];
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `/tasks/major-jobs/?user_id=${userId}`,
        method: "POST",
        body: {
          ...body,
          included_connection_ids: body.included_connection_ids ?? [],
        },
      }),
      invalidatesTags: (_r, _e, { userId }) => [{ type: "MajorJobs", id: userId }],
    }),

    updateMajorJob: builder.mutation<
      MajorJob,
      {
        majorJobId: number;
        body: Partial<{
          name: string;
          vendor: string;
          bot_kind: string;
          included_connection_ids: number[];
        }>;
        invalidateUserId: number;
      }
    >({
      query: ({ majorJobId, body }) => ({
        url: `/tasks/major-jobs/${majorJobId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { invalidateUserId }) => [{ type: "MajorJobs", id: invalidateUserId }],
    }),

    deleteMajorJob: builder.mutation<
      void,
      { majorJobId: number; invalidateUserId: number }
    >({
      query: ({ majorJobId }) => ({
        url: `/tasks/major-jobs/${majorJobId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { invalidateUserId }) => [{ type: "MajorJobs", id: invalidateUserId }],
    }),
    // Counted fresh for every confirmation: a cached answer could promise less
    // than the delete is about to take.
    getMajorJobDeletePreview: builder.query<MajorJobDeletePreview, number>({
      query: (majorJobId) => `/tasks/major-jobs/${majorJobId}/delete-preview/`,
      keepUnusedDataFor: 0,
    }),

    tasksBatchDelete: builder.mutation<
      { deleted_task_ids: number[]; missing_task_ids: number[] },
      { task_ids: number[] }
    >({
      query: ({ task_ids }) => ({
        url: "/tasks/batch/delete-tasks/",
        method: "POST",
        body: { task_ids },
      }),
      invalidatesTags: ["AdminUsers", "BotJobData"],
    }),

    taskJobsBatchDelete: builder.mutation<
      {
        deleted_job_ids: number[];
        deleted_task_ids: number[];
        artifacts_deleted_for_jobs: number[];
        roster_kept_for_job_ids?: number[];
        errors: { job_id: number; error: string }[];
      },
      { job_ids: number[] }
    >({
      query: (body) => ({
        url: "/tasks/batch/delete-jobs/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers", "BotJobData"],
    }),

    // Both log streams of a job, folded into the current state of every employee
    // and every file, filtered/counted/paged server-side. The raw *-logs endpoints
    // below stay as they are - they back the viewer's Raw switch.
    getJobLogRecords: builder.query<
      LogRecordsResponse,
      { jobId: number } & LogRecordsQuery
    >({
      query: ({ jobId, ...params }) =>
        `/tasks/jobs/${jobId}/log-records/${buildLogRecordsQuery(params)}`,
    }),

    // One task's slice of its job's logs: the records inside its CURRENT range,
    // wherever they were written (ranges are re-sliced when tasks are added).
    getTaskLogRecords: builder.query<
      LogRecordsResponse,
      { taskId: number } & LogRecordsQuery
    >({
      query: ({ taskId, ...params }) =>
        `/tasks/${taskId}/log-records/${buildLogRecordsQuery(params)}`,
    }),

    // The error screenshot a failing employee's record names: asked for when its
    // row is opened, never with the list. The link expires in minutes.
    getJobErrorScreenshot: builder.query<
      ErrorScreenshotLink,
      { jobId: number; name: string }
    >({
      query: ({ jobId, name }) =>
        `/tasks/jobs/${jobId}/error-screenshot/?name=${encodeURIComponent(name)}`,
      keepUnusedDataFor: 60,
    }),

    // Manually resolved (CAL-5982): one ERROR employee's files, and the
    // operator's word on which of them were uploaded by hand - or un-marked.
    getJobManualResolutionPreview: builder.query<
      ManualResolutionPreview,
      { jobId: number } & ManualResolutionKey
    >({
      query: ({ jobId, ...key }) =>
        `/tasks/jobs/${jobId}/manual-resolution/${buildManualResolutionQuery(key)}`,
    }),
    resolveJobEmployeeFiles: builder.mutation<
      ManualResolutionSaveResponse,
      { jobId: number } & ManualResolutionSaveRequest
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/manual-resolution/`,
        method: "POST",
        body,
      }),
      // The job row's counters follow once the worker recounts the logs.
      invalidatesTags: ["BotJobData"],
    }),

    getJobStatusLogs: builder.query<
      { logs: string; job_id: number },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/status-logs/`,
    }),

    getJobFileLogs: builder.query<
      { logs: string; job_id: number },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/file-logs/`,
    }),

    // One .zip bundling every per-task log zip of the job (manual inspection).
    getJobLogsArchive: builder.query<Blob, number>({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/logs-archive/`,
        // API Gateway only base64-decodes binary bodies when the request's Accept
        // matches a BinaryMediaType; without this the zip arrives as a corrupt string.
        headers: { Accept: "application/zip" },
        responseHandler: (response: Response) => response.blob(),
        cache: "no-cache",
      }),
    }),

    // How many stored archives at the job's folder are its own and how many belong to
    // other jobs sharing it - asked only when Clear logs is about to be used, because
    // reading a job must not touch S3.
    getJobLogsInventory: builder.query<
      { job_id: number; own: number; shared: number; shared_task_ids: number[] },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/logs-inventory/`,
    }),

    // Wipe the job's stored log archives so the next run re-processes everyone.
    // includeShared also takes the archives of other jobs in the same folder - which
    // the bot reads at startup too, so it is what a true full re-run needs.
    clearJobLogs: builder.mutation<
      { job_id: number; deleted: number; shared_skipped: number },
      { jobId: number; includeShared?: boolean }
    >({
      query: ({ jobId, includeShared }) => ({
        url: `/tasks/jobs/${jobId}/logs/${includeShared ? "?include_shared=1" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BotJobData"],
    }),

    // Runs the roster rows no task has ever run on. The server decides which
    // tasks those are, so the panel cannot restart the ones that already ran.
    runJobNewRows: builder.mutation<
      {
        message: string;
        task_ids: number[];
        queued_task_ids: number[];
        started_task_id: number | null;
      },
      { jobId: number; taskCount?: number }
    >({
      query: ({ jobId, taskCount }) => ({
        url: `/tasks/jobs/${jobId}/run-new-rows/`,
        method: "POST",
        headers: PANEL_RUN_HEADERS,
        body: taskCount ? { task_count: taskCount } : {},
      }),
    }),

    // Runs the roster-only task: it logs in, builds the roster (download,
    // delete) or the matches files (upload), publishes the total and processes
    // nobody. 409 roster_exists when the stored file would be reused; force runs
    // it anyway.
    runJobRosterTask: builder.mutation<
      {
        message: string;
        task_id: number;
        created: boolean;
        started_task_id: number | null;
      },
      { jobId: number; force?: boolean; confirm_reindex?: boolean }
    >({
      query: ({ jobId, force, confirm_reindex }) => ({
        url: `/tasks/jobs/${jobId}/roster-task/run/`,
        method: "POST",
        headers: PANEL_RUN_HEADERS,
        body: {
          ...(force ? { force: true } : {}),
          ...(confirm_reindex ? { confirm_reindex: true } : {}),
        },
      }),
      invalidatesTags: ["BotJobData"],
    }),

    getTaskStatusLogs: builder.query<{ logs: string; task_id: number }, number>({
      query: (taskId) => `/tasks/${taskId}/status-logs/`,
    }),

    getTaskFileLogs: builder.query<{ logs: string; task_id: number }, number>({
      query: (taskId) => `/tasks/${taskId}/file-logs/`,
    }),

    // Full task log history (the bot's all_logs file). Survives ECS task
    // restarts, unlike CloudWatch live logs. Frontend turns it into a download.
    getTaskAllLogs: builder.query<
      { logs: string; found: boolean; task_id: number },
      number
    >({
      query: (taskId) => `/tasks/${taskId}/all-logs/`,
    }),

    // CloudWatch history: no params → returns {local, range}; with start/end
    // (ms epoch) + optional next_token → returns {local, events, next_token}.
    getTaskCloudWatchLogs: builder.query<
      {
        local: boolean;
        range?: { first: number | null; last: number | null } | null;
        events?: Array<{ timestamp: number | null; message: string }>;
        next_token?: string | null;
      },
      { taskId: number; start?: number; end?: number; nextToken?: string }
    >({
      query: ({ taskId, start, end, nextToken }) => {
        const params = new URLSearchParams();
        if (start != null) params.append("start", String(start));
        if (end != null) params.append("end", String(end));
        if (nextToken) params.append("next_token", nextToken);
        const qs = params.toString();
        return `/tasks/${taskId}/cloudwatch-logs/${qs ? `?${qs}` : ""}`;
      },
    }),

    getJobMatchesFiles: builder.query<JobMatchesFilesResponse, number>({
      query: (jobId) => `/tasks/jobs/${jobId}/matches-files/`,
    }),

    getJobMatchesFileContent: builder.query<
      { filename: string; content: string },
      { jobId: number; filename: string }
    >({
      query: ({ jobId, filename }) => `/tasks/jobs/${jobId}/matches-files/${filename}/`,
    }),

    // The No matches / Several matches employees by state, for the CSV viewers.
    getJobUnmatchedReview: builder.query<UnmatchedReviewDetail, { jobId: number }>({
      query: ({ jobId }) => `/tasks/jobs/${jobId}/matches-files/review/`,
    }),

    // "No match found" on a whole file marks every employee still to review (or
    // clears the file's marks); the job row's summary follows the answer.
    markJobUnmatchedReview: builder.mutation<
      UnmatchedReviewMarkResponse,
      { jobId: number; kind: MergeReportKind; action: UnmatchedReviewAction }
    >({
      query: ({ jobId, kind, action }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/review/`,
        method: "POST",
        body: { kind, action },
      }),
      invalidatesTags: ["BotJobData"],
    }),

    deleteJobMatchesFile: builder.mutation<void, { jobId: number; filename: string }>({
      query: ({ jobId, filename }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/${filename}/`,
        method: "DELETE",
      }),
    }),

    presignJobMatchesMergeUpload: builder.mutation<
      {
        upload_url: string;
        key: string;
        staging_id: string;
        file_name: string;
        headers: Record<string, string>;
      },
      { jobId: number; file_name: string; content_type?: string }
    >({
      query: ({ jobId, file_name, content_type }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/merge/presign/`,
        method: "POST",
        body: {
          file_name,
          content_type: content_type || "application/octet-stream",
        },
      }),
    }),

    // The reviewed No matches / Several matches files, one staged upload per
    // slot. The backend checks both before writing: a 400 `merge_files_invalid`
    // carries a MergeFileReport per slot and nothing is merged.
    mergeJobMatchesReports: builder.mutation<
      MergeReportsResponse,
      { jobId: number; files: Partial<Record<MergeReportKind, { staging_key: string }>> }
    >({
      query: ({ jobId, files }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/merge/`,
        method: "POST",
        body: { files },
      }),
    }),

    // Send rejected files to SFTP: readiness and preview, the start (SFTP row
    // created or reused, copy handed to a worker), then the copy's progress
    // is polled off the same GET while `export.state` is "running".
    getJobSftpExport: builder.query<
      SftpExportStatus,
      { jobId: number; preview?: boolean; includeFiles?: boolean; refresh?: boolean }
    >({
      query: ({ jobId, preview, includeFiles, refresh }) => {
        const params = new URLSearchParams();
        if (preview) params.set("preview", "1");
        if (includeFiles) params.set("include_files", "1");
        if (refresh) params.set("refresh", "1");
        const qs = params.toString();
        return `/tasks/jobs/${jobId}/sftp-export/${qs ? `?${qs}` : ""}`;
      },
    }),
    startJobSftpExport: builder.mutation<
      SftpExportStartResponse,
      { jobId: number; sftp: SftpExportStartRequest }
    >({
      query: ({ jobId, sftp }) => ({
        url: `/tasks/jobs/${jobId}/sftp-export/`,
        method: "POST",
        body: { sftp },
      }),
      invalidatesTags: ["SftpConnections"],
    }),
    getJobSftpExportFiles: builder.query<
      SftpExportFilesResponse,
      { jobId: number; scope?: "job" | "root" }
    >({
      query: ({ jobId, scope }) =>
        `/tasks/jobs/${jobId}/sftp-export/files/${scope === "root" ? "?scope=root" : ""}`,
    }),
    getJobSftpExportDownloadUrl: builder.query<
      { url: string; expires_in: number; name: string },
      { jobId: number; key: string }
    >({
      query: ({ jobId, key }) =>
        `/tasks/jobs/${jobId}/sftp-export/files/download/?key=${encodeURIComponent(key)}`,
    }),

    // The stored file a run reuses, and the reset that makes the next run
    // collect it again.
    getJobRoster: builder.query<JobRosterInfo, number>({
      query: (jobId) => `/tasks/jobs/${jobId}/roster/`,
    }),
    /** The roster as rows and their state - the whole file counted, one page read. */
    getJobRosterRecords: builder.query<RosterFileRecordsResponse, RosterFileQuery>({
      query: ({ jobId, ...params }) =>
        `/tasks/jobs/${jobId}/roster/records/` +
        buildRosterFileQuery({ jobId, ...params }),
    }),
    /** S3 hands the file over directly: the API response is capped at 6 MB. */
    getJobRosterDownloadUrl: builder.query<
      { url: string | null; inline: boolean; filename?: string },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/roster/download-url/`,
    }),
    // 409 confirm_reindex_required when tasks of the job have already run: the
    // next run collects a new file, which puts the employees on other rows.
    deleteJobRoster: builder.mutation<
      { deleted_keys: string[]; sibling_job_ids: number[] },
      { jobId: number; confirm_reindex?: boolean }
    >({
      query: ({ jobId, confirm_reindex }) => ({
        url: `/tasks/jobs/${jobId}/roster/`,
        method: "DELETE",
        body: confirm_reindex ? { confirm_reindex: true } : {},
      }),
      invalidatesTags: ["BotJobData"],
    }),
    // Deals the roster out again across the job's tasks, clearing the slices
    // they claimed. Starts no bot - the operator presses Run afterwards.
    recoverJobRoster: builder.mutation<
      { message: string; tasks: unknown[]; roster_state: RosterState | null },
      number
    >({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/roster/recover/`,
        method: "POST",
      }),
      invalidatesTags: ["BotJobData"],
    }),
    // Manual replacement of the Matches file (upload jobs) or the roster
    // (download, delete): presign, PUT, then one of the replace calls.
    presignJobArtifactUpload: builder.mutation<
      {
        upload_url: string;
        key: string;
        staging_id: string;
        file_name: string;
        headers: Record<string, string>;
      },
      { jobId: number; file_name: string; content_type: string; purpose: "matches" | "roster" }
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/artifacts/upload/presign/`,
        method: "POST",
        body,
      }),
    }),
    replaceJobMatches: builder.mutation<
      {
        rows: number;
        replaced: boolean;
        actual_total_employees: number | null;
        matches_filename: string;
      },
      { jobId: number; staging_key: string; file_name?: string; confirm_reindex?: boolean }
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/replace/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),
    replaceJobRoster: builder.mutation<
      {
        rows: number;
        key: string;
        actual_total_employees: number | null;
        sibling_job_ids: number[];
      },
      { jobId: number; staging_key: string; confirm_reindex?: boolean }
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/roster/replace/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotJobData"],
    }),
    getJobSsnReadiness: builder.query<SsnReadiness, number>({
      query: (jobId) => `/tasks/jobs/${jobId}/ssn-readiness/`,
    }),
    // The pre-run check of the folders against the SSN files. POST runs it
    // again in the background; the job row (getTaskJobs) carries the result
    // in matches_preflight once the websocket says the job changed.
    getJobMatchesPreflight: builder.query<
      { job_id: number; matches_preflight: MatchesPreflight | null },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/matches-preflight/`,
    }),
    runJobMatchesPreflight: builder.mutation<
      { job_id: number; matches_preflight: MatchesPreflight },
      number
    >({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/matches-preflight/`,
        method: "POST",
      }),
      invalidatesTags: ["BotJobData"],
    }),
    // SSN Updater: the estimate and the last run (polled off the same GET while
    // run.state is "running"), and the start.
    getJobSsnUpdater: builder.query<
      SsnUpdaterStatus,
      { jobId: number; includeEntries?: boolean }
    >({
      query: ({ jobId, includeEntries }) =>
        `/tasks/jobs/${jobId}/ssn-updater/${includeEntries ? "?include_entries=1" : ""}`,
    }),
    startJobSsnUpdater: builder.mutation<
      { run: SsnUpdaterRun; queued: boolean },
      { jobId: number; allowSimilar: boolean }
    >({
      query: ({ jobId, allowSimilar }) => ({
        url: `/tasks/jobs/${jobId}/ssn-updater/`,
        method: "POST",
        body: { allow_similar: allowSimilar },
      }),
    }),
    getMajorJobSsnReadiness: builder.query<
      SsnReadiness,
      { majorJobId: number; path: string; useSsnMatch: boolean }
    >({
      query: ({ majorJobId, path, useSsnMatch }) =>
        `/tasks/major-jobs/${majorJobId}/ssn-readiness/?path=${encodeURIComponent(path)}&use_ssn_match=${useSsnMatch ? "true" : "false"}`,
    }),
    // The stored Chrome profiles of the client on this vendor (shared by every
    // job of the vendor); deleting them costs one fresh login per bot.
    getMajorJobBrowserProfiles: builder.query<BrowserProfilesInfo, number>({
      query: (majorJobId) => `/tasks/major-jobs/${majorJobId}/browser-profiles/`,
    }),
    deleteMajorJobBrowserProfiles: builder.mutation<
      BrowserProfilesInfo & { deleted_keys: string[] },
      { majorJobId: number; profileId?: number }
    >({
      query: ({ majorJobId, profileId }) => ({
        url: `/tasks/major-jobs/${majorJobId}/browser-profiles/${
          profileId != null ? `?profile_id=${profileId}` : ""
        }`,
        method: "DELETE",
      }),
    }),

    getMajorJobSsnJob: builder.query<
      {
        job: Record<string, unknown> | null;
        task: Record<string, unknown> | null;
        ssn_file_exists: boolean | null;
      },
      number
    >({
      query: (majorJobId) => `/tasks/major-jobs/${majorJobId}/ssn-job/`,
    }),

    createMajorJobSsnJob: builder.mutation<
      {
        job: Record<string, unknown> | null;
        task: Record<string, unknown> | null;
        ssn_file_exists: boolean | null;
      },
      {
        majorJobId: number;
        connection_id?: number;
        additional_options?: string;
      }
    >({
      query: ({ majorJobId, connection_id, additional_options }) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/`,
        method: "POST",
        body: {
          ...(connection_id != null ? { connection_id } : {}),
          ...(additional_options != null ? { additional_options } : {}),
        },
      }),
    }),

    // The SSN job is kept out of the job list, so the generic job PATCH never
    // reaches it; this edits its options in place.
    updateMajorJobSsnJob: builder.mutation<
      {
        job: Record<string, unknown> | null;
        task: Record<string, unknown> | null;
        ssn_file_exists: boolean | null;
      },
      { majorJobId: number; additional_options: string }
    >({
      query: ({ majorJobId, additional_options }) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/`,
        method: "PATCH",
        body: { additional_options },
      }),
    }),

    deleteMajorJobSsnJob: builder.mutation<void, number>({
      query: (majorJobId) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/`,
        method: "DELETE",
      }),
    }),

    deleteMajorJobSsnFile: builder.mutation<void, number>({
      query: (majorJobId) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/s3-file/`,
        method: "DELETE",
      }),
    }),

    getMajorJobSsnFileContent: builder.query<
      { filename: string; content: string },
      number
    >({
      query: (majorJobId) => `/tasks/major-jobs/${majorJobId}/ssn-job/s3-file/`,
    }),

    /** The SSN file as rows and counters - the whole file counted, one page read. */
    getMajorJobSsnFileRecords: builder.query<
      SsnFileRecordsResponse,
      { majorJobId: number } & SsnFileQuery
    >({
      query: ({ majorJobId, ...params }) =>
        `/tasks/major-jobs/${majorJobId}/ssn-job/s3-file/records/` +
        buildSsnFileQuery(params),
    }),

    /** S3 hands the file over directly: the API response is capped at 6 MB. */
    getMajorJobSsnFileDownloadUrl: builder.query<
      { url: string | null; inline: boolean },
      number
    >({
      query: (majorJobId) =>
        `/tasks/major-jobs/${majorJobId}/ssn-job/s3-file/download-url/`,
    }),
  }),
});

export { adminUsersApi };

export const {
  useGetAdminUsersQuery,
  useLazyGetAdminUsersQuery,
  useGetAdminUserQuery,
  useLazyCheckPlatformUsernameAvailabilityQuery,
  useLazyCheckPlatformEmailAvailabilityQuery,
  useUpdateAdminUserMutation,
  useCreateAdminUserMutation,
  useArchiveAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useGenerateAdminUserPasswordMutation,
  useGetAdminUserFoldersQuery,
  useGetAssignableFoldersQuery,
  useGetAssignableIntegrationsQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
  useUpdateFolderSubfoldersMutation,
  useLazyGetFolderSubfoldersQuery,
  useGetAdminUserIntegrationsQuery,
  useGrantIntegrationAccessMutation,
  useRevokeIntegrationAccessMutation,
  useGetAdminChatBotProfileQuery,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
  useUpdateAdminUserPermissionsMutation,
  useGetBackendUsersQuery,
  useLazyGetBackendUsersQuery,
  useLazyCheckBackendUsernameAvailabilityQuery,
  useLazyCheckBackendEmailAvailabilityQuery,
  useGetBackendUserFoldersQuery,
  useGetBackendUserStorageFoldersQuery,
  useGetBackendUserIntegrationsQuery,
  useGetBackendUserBotConnectionsQuery,
  useLazyGetBackendUserBotConnectionsQuery,
  useCreateBackendUserBotConnectionMutation,
  useCreateConnectionTaskMutation,
  useAddTasksToJobMutation,
  useReduceJobTasksMutation,
  useCreateTasksBatchMutation,
  useGetBotRegistryQuery,
  useUpdateAdminConnectionMutation,
  useDeleteAdminConnectionMutation,
  useGetTaskJobsQuery,
  useGetTaskJobQuery,
  useLazyGetTaskJobQuery,
  useUpdateTaskJobMutation,
  useRefreshTaskJobStatsMutation,
  useRecalculateTaskJobRangesMutation,
  useDeleteTaskJobMutation,
  useRunTaskMutation,
  useRunTasksBatchMutation,
  useStopTaskMutation,
  useStopTasksBatchMutation,
  useSetTaskPendingMutation,
  useGetAdminTaskQuery,
  useUpdateAdminTaskMutation,
  useDeleteTaskMutation,
  useGetTask2FAStatusQuery,
  useSubmitTask2FACodeMutation,
  useGetTaskLiveViewQuery,
  useReleaseTaskLiveViewMutation,
  useCreateBackendUserMutation,
  useUpdateBackendUserMutation,
  useDeleteBackendUserMutation,
  useGetMajorJobsQuery,
  useCreateMajorJobMutation,
  useUpdateMajorJobMutation,
  useDeleteMajorJobMutation,
  useGetMajorJobDeletePreviewQuery,
  useTasksBatchDeleteMutation,
  useTaskJobsBatchDeleteMutation,
  useLazyGetJobLogRecordsQuery,
  useLazyGetJobLogsInventoryQuery,
  useLazyGetTaskLogRecordsQuery,
  useGetJobErrorScreenshotQuery,
  useGetJobManualResolutionPreviewQuery,
  useResolveJobEmployeeFilesMutation,
  useLazyGetJobStatusLogsQuery,
  useLazyGetJobFileLogsQuery,
  useLazyGetJobLogsArchiveQuery,
  useClearJobLogsMutation,
  useRunJobNewRowsMutation,
  useRunJobRosterTaskMutation,
  useLazyGetTaskStatusLogsQuery,
  useLazyGetTaskFileLogsQuery,
  useLazyGetTaskAllLogsQuery,
  useLazyGetTaskCloudWatchLogsQuery,
  useLazyGetJobMatchesFilesQuery,
  useLazyGetJobMatchesFileContentQuery,
  useLazyGetJobUnmatchedReviewQuery,
  useMarkJobUnmatchedReviewMutation,
  useDeleteJobMatchesFileMutation,
  usePresignJobMatchesMergeUploadMutation,
  useMergeJobMatchesReportsMutation,
  useGetJobSftpExportQuery,
  useLazyGetJobSftpExportQuery,
  useStartJobSftpExportMutation,
  useLazyGetJobSftpExportFilesQuery,
  useLazyGetJobSftpExportDownloadUrlQuery,
  useLazyGetJobRosterQuery,
  useLazyGetJobRosterRecordsQuery,
  useLazyGetJobRosterDownloadUrlQuery,
  useDeleteJobRosterMutation,
  useRecoverJobRosterMutation,
  usePresignJobArtifactUploadMutation,
  useReplaceJobMatchesMutation,
  useReplaceJobRosterMutation,
  useLazyGetJobSsnReadinessQuery,
  useLazyGetMajorJobSsnReadinessQuery,
  useLazyGetJobMatchesPreflightQuery,
  useRunJobMatchesPreflightMutation,
  useGetJobSsnUpdaterQuery,
  useLazyGetJobSsnUpdaterQuery,
  useStartJobSsnUpdaterMutation,
  useLazyGetMajorJobBrowserProfilesQuery,
  useDeleteMajorJobBrowserProfilesMutation,
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useLazyGetMajorJobSsnJobQuery,
  useCreateMajorJobSsnJobMutation,
  useUpdateMajorJobSsnJobMutation,
  useDeleteMajorJobSsnJobMutation,
  useDeleteMajorJobSsnFileMutation,
  useLazyGetMajorJobSsnFileContentQuery,
  useLazyGetMajorJobSsnFileDownloadUrlQuery,
  useLazyGetMajorJobSsnFileRecordsQuery,
  useScheduleJobRunMutation,
  useCancelJobRunScheduleMutation,
  useScheduleTaskRunMutation,
  useCancelTaskRunScheduleMutation,
  useArchiveClientMutation,
  useLazyCheckClientCodeAvailabilityQuery,
  useLazyCheckClientNameAvailabilityQuery,
} = adminUsersApi;
